import { Avatar, FlexboxGrid } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useEffect, useState } from 'react'

import { getSmallPreview, imageLoader } from '../../utils/images'
import { ScreenSize } from '../../utils/constants'
import { Product, Supplier } from '../../utils/types/Product'
import useUrls from '../../services/useUrls'
import useIsOnMobile from '../../services/useIsOnMobile'
import { getProductHeadingText } from '../../lib/text'
import { ReviewStarsWithDisplayedAverage } from './ReviewsSection/ReviewStars'
import useReviewFetch from '../../services/useReviewFetch'
import { getAverageOfReviews } from '../../utils/reviewUtils'
import { getSalesUnitAmounts } from '../../lib/salesUnit'
import { getSupplierLogo } from '../../lib/supplier'
import SaveProductButton from '../MyProducts/SaveProductButton'
import AddToOrderListButton from '../Buttons/AddToOrderListButton'

import styles from '../../styles/ProductSupplierHeading.module.less'

interface ProductSupplierHeadingProps {
  product: Product
  supplier?: Supplier
  handleScrollToReviews: () => void
}

const analyticsLocation = 'single_product'

const ProductSupplierHeading: React.FC<ProductSupplierHeadingProps> = (props) => {
  const { supplier, product, handleScrollToReviews } = props

  const { urlT } = useUrls()
  const { t } = useTranslation('products')
  const isOnMobile = useIsOnMobile(ScreenSize.md)
  const { ratingQuantities } = useReviewFetch(product.id)
  const sppProductHeadingV3FeatureFlag = useFeatureIsOn('spp-product-heading-v3')

  const [showAddToOrderList, setShowAddToOrderList] = useState(false)

  const reviewsAverage = getAverageOfReviews(ratingQuantities)
  const supplierUrl = urlT(`/supplier/${supplier?.['supplier.code']}`)
  const supplierName = supplier?.['supplier.label']
  const productHeading = supplier ? getProductHeadingText(product, supplier) : ''
  const supplierLogo = getSupplierLogo(supplier?.media)

  const getProductHeading = () => {
    if (sppProductHeadingV3FeatureFlag) {
      return getSalesUnitAmounts(product, t) === null
        ? productHeading
        : `${productHeading}, ${getSalesUnitAmounts(product, t)}`
    }

    return productHeading
  }

  const handleSaveProductInteraction = () => {
    if (isOnMobile) {
      setShowAddToOrderList(!showAddToOrderList)
    }
  }

  const handleMouseEnter = () => {
    if (!isOnMobile) {
      setShowAddToOrderList(true)
    }
  }

  const handleMouseLeave = () => {
    if (!isOnMobile) {
      setShowAddToOrderList(false)
    }
  }

  // Reset showAddToOrderList when product changes
  useEffect(() => {
    setShowAddToOrderList(false)
  }, [product.id])

  return (
    <>
      <h1 className={isOnMobile ? 'h3' : 'h2'}>
        {getProductHeading()}
      </h1>

      {!!reviewsAverage && (
        <div className="margin-bottom-spacer">
          <ReviewStarsWithDisplayedAverage
            average={reviewsAverage}
            handleScrollToReviews={handleScrollToReviews}
          />
        </div>
      )}
      <FlexboxGrid
        align="middle"
        justify="space-between"
        className="flex-flow-row"
      >
        <FlexboxGrid.Item className="flex-shrink-col-dynamic margin-right-spacer">
          <FlexboxGrid
            align="middle"
            className={isOnMobile ? 'flex-flow-row' : 'margin-bottom-spacer'}
          >
            <Link
              href={supplierUrl}
              className={styles['logo-link']}
            >
              <Avatar
                alt={`${supplierName} logo`}
                size="md"
                circle
                data-testid="supplier-logo"
                className={styles['supplier-logo']}
                src={imageLoader(getSmallPreview(supplierLogo))}
              />
            </Link>
            <FlexboxGrid align="middle">
              <p className="h5 margin-bottom-zero-important margin-right-spacer-half">{supplierName}</p>
              <Link
                href={supplierUrl}
                className={styles['visit-store']}
              >
                {t('visit store')}
              </Link>
            </FlexboxGrid>
          </FlexboxGrid>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item>
          <FlexboxGrid>
            <FlexboxGrid.Item
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              className={styles['catalog-icon-container']}
            >
              {showAddToOrderList && (
                <>
                  <AddToOrderListButton
                    product={product}
                    className={styles['catalog-icon']}
                    onSave={() => setShowAddToOrderList(false)}
                    onClose={() => setShowAddToOrderList(false)}
                  />
                  <span className={styles['catalog-text']}>{t('Add to Catalogs')}</span>
                </>
              )}
            </FlexboxGrid.Item>
            <FlexboxGrid.Item
              className={styles['save-product-button-container']}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <SaveProductButton
                analyticsLocation={analyticsLocation}
                // Mobile fix so that it won't save on first click
                product={showAddToOrderList ? product : undefined}
                appearance="ghost"
                onClick={showAddToOrderList ? undefined : handleSaveProductInteraction}
                className={styles['save-product-button-overlay']}
              />
              {showAddToOrderList && <span className={styles['save-product-text']}>{t('Save product')}</span>}
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </>
  )
}

export default ProductSupplierHeading
